'use client';

import { produce } from 'immer';
import Linkify from 'linkify-react';
import { InfoIcon } from 'lucide-react';

import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { Label } from '@/components/ui/label';

import { Tooltip } from '@/shared/components/tooltip';

import BorderTextField from '@features/form-editing/components/common/fields/border-text-field';
import { FORM_FIELD_TYPE_DISABLED_TOOLTIPS } from '@features/form-editing/components/constants/form-edit-constants';
import { IAddFieldDropdownOption } from '@features/form-editing/types/form-editing-types';

import EditableText from '@/shared/components/editable-text';
import { HotkeysScopeProvider } from '@/shared/components/hotkeys';
import { ISFormInputField } from '@/shared/services/forms/forms.types';
import { KeyboardKeys } from '@/shared/utils/navigation';

import { cn } from '@/lib/utils';

import FieldActionBar from './field-action-bar';
import FieldInputRenderer, {
	FieldInputRendererRef,
	SELECT_FIELD_TYPES,
} from './field-input-renderer';

type FieldEditSectionProps = {
	nonEditable?: boolean;
	formId: string;
	inputField: ISFormInputField;
	onChangeInputField: (updatedInputField: ISFormInputField) => void;
	onClickCopyInputField: (inputField: ISFormInputField) => void;
	onClickDeleteInputField: (inputFieldId: string) => void;
	editMode?: boolean;
	onFocus?: () => void;
	onBlur?: () => void;
	isFocusedField?: boolean;
	fieldTypeOptions?: IAddFieldDropdownOption[];
	hideDescription?: boolean;
	containerClassName?: string;
	labelClassName?: string;
	labelTooltip?: string;
	inputTooltip?: string;
	disableShortcuts?: boolean;
};

function FieldEditSection({
	nonEditable,
	onFocus,
	isFocusedField,
	inputField,
	editMode,
	onChangeInputField,
	onClickCopyInputField,
	onClickDeleteInputField,
	formId,
	fieldTypeOptions,
	hideDescription,
	containerClassName,
	labelClassName,
	labelTooltip,
	inputTooltip,
	disableShortcuts = false,
}: FieldEditSectionProps) {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const descriptionFieldRef = useRef<HTMLTextAreaElement | null>(null);
	const fieldRendererRef = useRef<FieldInputRendererRef | null>(null);

	const { question, description, required: isRequired } = inputField;

	const netFocusedField = isFocusedField && !nonEditable;
	const isSelectField = SELECT_FIELD_TYPES.has(inputField.type);

	const disabledTooltip = useMemo(
		() => FORM_FIELD_TYPE_DISABLED_TOOLTIPS[inputField.preDefinedInputType ?? ''],
		[inputField.preDefinedInputType]
	);

	const handleOnChangeQuestion = useCallback(
		(value: string) => {
			onChangeInputField(
				produce(inputField, (draft) => {
					draft.question = value;
				})
			);
		},
		[onChangeInputField, inputField]
	);

	const handleOnChangeDescription = useCallback(
		(value: string) => {
			onChangeInputField(
				produce(inputField, (draft) => {
					draft.description = value;
				})
			);
		},
		[onChangeInputField, inputField]
	);

	const handleUpdateIsRequired = useCallback(
		(value: boolean) => {
			onChangeInputField(
				produce(inputField, (draft) => {
					draft.required = value;
				})
			);
		},
		[onChangeInputField, inputField]
	);

	const handleUpdateOptions = useCallback(
		(updatedOptions: string[]) => {
			onChangeInputField(
				produce(inputField, (draft) => {
					draft.options = updatedOptions;
				})
			);
		},
		[onChangeInputField, inputField]
	);

	const handleAllowOtherChange = useCallback(
		(value: boolean) => {
			onChangeInputField(
				produce(inputField, (draft) => {
					draft.allowOther = value;
				})
			);
		},
		[onChangeInputField, inputField]
	);

	const handleOnClickAddDescription = useCallback(() => {
		onChangeInputField(
			produce(inputField, (draft) => {
				draft.description = draft.description === '' ? undefined : '';
			})
		);
		setTimeout(() => descriptionFieldRef.current?.focus());
	}, [onChangeInputField, inputField]);

	const handleOnClickCopyInputField = useCallback(() => {
		onClickCopyInputField(inputField);
	}, [onClickCopyInputField, inputField]);

	const handleOnClickDeleteInputField = useCallback(() => {
		onClickDeleteInputField(inputField.id);
	}, [onClickDeleteInputField, inputField.id]);

	const handleOnFocusTextarea = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
		e.target.select();
	}, []);

	const handleOnKeyDownInputSection = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			const isModifierKeyPressed = e.shiftKey || e.ctrlKey || e.altKey || e.metaKey;
			if (e.key === 'Enter' && !isModifierKeyPressed) {
				e.preventDefault();
				onFocus?.();
			}
		},
		[onFocus]
	);

	const focusableInputSectionProps = useMemo(
		() => {
			if (editMode && !nonEditable) {
				return {
					role: 'button' as const,
					tabIndex: 0,
					onClick: () => onFocus?.(),
					onKeyDown: handleOnKeyDownInputSection,
					onFocus: () => onFocus?.(),
				};
			}
			// Non-editable in view-only mode (e.g. fixed template fields): fully inert —
			// reordering is handled by the drag handle outside this component.
			if (!editMode && nonEditable) return {};
			return { onClick: () => onFocus?.() };
		},
		[editMode, nonEditable, onFocus, handleOnKeyDownInputSection]
	);

	useEffect(() => {
		if (!isFocusedField) return;

		containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

		setTimeout(() => {
			const containerEle = containerRef.current;
			if (!containerEle) return;

			const isContainerEleFocused =
				document.activeElement === containerEle || !containerEle.contains(document.activeElement);
			if (!isContainerEleFocused) return;

			containerEle.querySelector('textarea')?.focus();
		}, 250);
	}, [isFocusedField]);

	return (
		<HotkeysScopeProvider
			{...focusableInputSectionProps}
			className={`flex w-full flex-col gap-0 focus-visible:outline-none ${containerClassName ?? 'px-6 py-2 md:px-16'}`}
			ref={containerRef}
			scope={`${formId}-${inputField.id}`}
		>
			<div className="mb-3">
				{!!netFocusedField ? (
					<BorderTextField
						placeholder="Question"
						value={question}
						onChangeValue={handleOnChangeQuestion}
						onFocus={handleOnFocusTextarea}
						editMode={editMode}
						nonEditable={nonEditable}
						customInputClassName={cn('text-lg font-semibold', labelClassName)}
						customParentClassName=""
						type="TEXTAREA_WITH_BOTTOM_LINE"
						onKeyDown={(e) => {
							if (e.key === KeyboardKeys.Enter && isSelectField && !e.shiftKey) {
								fieldRendererRef.current?.focusFirstOption();
							} else if (
								e.key === KeyboardKeys.Escape ||
								(e.key === KeyboardKeys.Enter && !e.shiftKey)
							) {
								containerRef.current?.focus();
							}
						}}
					/>
				) : (
					<Label className={cn('flex items-center gap-1.5 text-lg font-semibold', labelClassName)}>
						<span className="inline-block whitespace-pre-line [&_a]:text-primary-blue">
							<Linkify options={{ target: '_blank' }}>{question}</Linkify>
							<span className="font-normal text-primary/70">{isRequired ? ' *' : ''}</span>
						</span>
						{labelTooltip && (
							<Tooltip text={labelTooltip}>
								<InfoIcon className="size-3.5 shrink-0 text-slate-400" />
							</Tooltip>
						)}
					</Label>
				)}

				{!hideDescription &&
					typeof description === 'string' &&
					(netFocusedField || description) && (
						<EditableText
							value={description}
							onChange={handleOnChangeDescription}
							onFocus={handleOnFocusTextarea}
							onKeyDown={(e) => {
								if (e.key === KeyboardKeys.Escape) containerRef.current?.focus();
							}}
							placeholder="Description"
							className="mb-1 mt-2 text-sm font-normal text-primary"
							textareaClassName="mt-2 text-sm font-normal"
							ref={descriptionFieldRef}
						/>
					)}
			</div>

			{inputTooltip ? (
				<Tooltip text={inputTooltip} side="top">
					<div className="inline-block w-fit max-w-full">
						<FieldInputRenderer
							ref={fieldRendererRef}
							inputField={inputField}
							editMode={editMode}
							netFocusedField={!!netFocusedField}
							onChangeOptions={handleUpdateOptions}
							onAllowOtherChange={handleAllowOtherChange}
						/>
					</div>
				</Tooltip>
			) : (
				<FieldInputRenderer
					ref={fieldRendererRef}
					inputField={inputField}
					editMode={editMode}
					netFocusedField={!!netFocusedField}
					onChangeOptions={handleUpdateOptions}
					onAllowOtherChange={handleAllowOtherChange}
				/>
			)}

			{editMode && isFocusedField && (
				<FieldActionBar
					inputField={inputField}
					formId={formId}
					netFocusedField={!!netFocusedField}
					nonEditable={nonEditable}
					isRequired={!!isRequired}
					disabledTooltip={disabledTooltip}
					onUpdateIsRequired={handleUpdateIsRequired}
					onClickAddDescription={handleOnClickAddDescription}
					onClickCopy={handleOnClickCopyInputField}
					onClickDelete={handleOnClickDeleteInputField}
					onChangeInputField={onChangeInputField}
					fieldTypeOptions={fieldTypeOptions}
					hideDescription={hideDescription}
					disableShortcuts={disableShortcuts}
				/>
			)}
		</HotkeysScopeProvider>
	);
}

export type { FieldEditSectionProps };
export default FieldEditSection;
